'use client';

import { useTranslations } from 'next-intl';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import { alpha, useTheme } from '@mui/material/styles';

/**
 * Sample conversation preview showing bot Q&A dialogue
 *
 * Used in Variant A (Conversation First) and Variant C (Split Screen Desktop)
 * Demonstrates bot quality through realistic dialogue examples.
 *
 * Story 6.1: Marketing Landing Page with A/B Variants
 */

interface ConversationMessage {
  id: string;
  text: string;
  isUser: boolean;
  sourceHint?: string;
}

interface ConversationPreviewProps {
  /** Maximum number of messages to display */
  maxMessages?: number;
  /** Custom conversation messages (overrides default) */
  messages?: ConversationMessage[];
  /** Whether to show source hints on hover */
  showSourceHints?: boolean;
  /** Maximum height with scrolling */
  maxHeight?: number | string;
}

export function ConversationPreview({
  maxMessages = 8,
  messages: customMessages,
  showSourceHints = true,
  maxHeight = 500,
}: ConversationPreviewProps) {
  const t = useTranslations('creator.landing.conversationPreview');

  // Default sample conversation demonstrating bot capabilities
  const defaultMessages: ConversationMessage[] = [
    {
      id: '1',
      text: t('messages.user1'),
      isUser: true,
    },
    {
      id: '2',
      text: t('messages.bot1'),
      isUser: false,
      sourceHint: t('sourceHints.eventDetails'),
    },
    {
      id: '3',
      text: t('messages.user2'),
      isUser: true,
    },
    {
      id: '4',
      text: t('messages.bot2'),
      isUser: false,
      sourceHint: t('sourceHints.eventDetails'),
    },
    {
      id: '5',
      text: t('messages.user3'),
      isUser: true,
    },
    {
      id: '6',
      text: t('messages.bot3'),
      isUser: false,
      sourceHint: t('sourceHints.parkingInfo'),
    },
    {
      id: '7',
      text: t('messages.user4'),
      isUser: true,
    },
    {
      id: '8',
      text: t('messages.bot4'),
      isUser: false,
      sourceHint: t('sourceHints.foodVendors'),
    },
  ];

  const messages = customMessages || defaultMessages;
  const displayMessages = messages.slice(0, maxMessages);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        maxHeight,
        overflowY: 'auto',
        pr: 1,
        // Custom scrollbar styling
        '&::-webkit-scrollbar': {
          width: 6,
        },
        '&::-webkit-scrollbar-track': {
          bgcolor: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          bgcolor: 'grey.300',
          borderRadius: 3,
        },
      }}
    >
      {displayMessages.map((message) => (
        <MessageBubble
          key={message.id}
          message={message}
          showSourceHint={showSourceHints && !message.isUser}
        />
      ))}
    </Box>
  );
}

interface MessageBubbleProps {
  message: ConversationMessage;
  showSourceHint: boolean;
}

function MessageBubble({ message, showSourceHint }: MessageBubbleProps) {
  const theme = useTheme();
  const isRtl = theme.direction === 'rtl';
  // Keep user bubbles on the physical right and bot bubbles on the physical left
  // in BOTH directions. flex-start/flex-end follow the inline axis, which flips
  // under RTL, so invert the logical value when RTL to preserve the visual side.
  const userAlign = isRtl ? 'flex-start' : 'flex-end';
  const botAlign = isRtl ? 'flex-end' : 'flex-start';
  const bubble = (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        maxWidth: '85%',
        alignSelf: message.isUser ? userAlign : botAlign,
        bgcolor: message.isUser
          ? 'primary.main'
          : (theme) => alpha(theme.palette.primary.main, 0.08),
        color: message.isUser ? 'primary.contrastText' : 'text.primary',
        borderRadius: 2,
        borderTopRightRadius: message.isUser ? 0 : 2,
        borderTopLeftRadius: message.isUser ? 2 : 0,
        cursor: showSourceHint && message.sourceHint ? 'help' : 'default',
        transition: 'transform 0.15s ease',
        '&:hover': showSourceHint && message.sourceHint ? {
          transform: 'scale(1.01)',
        } : undefined,
      }}
    >
      <Typography
        variant="body2"
        sx={{
          fontSize: { xs: '0.875rem', md: '0.9375rem' },
          lineHeight: 1.5,
        }}
      >
        {message.text}
      </Typography>
    </Paper>
  );

  if (showSourceHint && message.sourceHint) {
    return (
      <Tooltip
        title={
          <Box sx={{ p: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 500 }}>
              Source: {message.sourceHint}
            </Typography>
          </Box>
        }
        arrow
        placement="left"
      >
        {bubble}
      </Tooltip>
    );
  }

  return bubble;
}
